import { useEffect, useState } from 'react';
import {
  createApp,
  listApps,
  updateApp,
  type ApplovinConfig,
  type App as AppRow,
  type LineSlot,
} from '../api';

const MAX_LINE_SLOTS = 5;

export default function Apps() {
  const [apps, setApps] = useState<AppRow[]>([]);
  const [pkg, setPkg] = useState('');
  const [name, setName] = useState('');
  const [brand, setBrand] = useState('');
  const [editing, setEditing] = useState<string | null>(null);
  const [editName, setEditName] = useState('');
  const [editBrand, setEditBrand] = useState('');
  const [expandedApplovin, setExpandedApplovin] = useState<string | null>(null);
  const [applovinDraft, setApplovinDraft] = useState<ApplovinConfig>({});
  const [expandedLine, setExpandedLine] = useState<string | null>(null);
  const [lineDraft, setLineDraft] = useState<LineSlot[]>([]);
  const [err, setErr] = useState<string | null>(null);

  async function load() {
    try {
      setErr(null);
      const r = await listApps();
      setApps(r.apps);
    } catch (e) {
      setErr(String(e));
    }
  }
  useEffect(() => void load(), []);

  async function onCreate(e: React.FormEvent) {
    e.preventDefault();
    if (!pkg.trim() || !name.trim()) return;
    try {
      await createApp({
        package: pkg.trim(),
        name: name.trim(),
        brand: brand.trim() || undefined,
      });
      setPkg('');
      setName('');
      setBrand('');
      await load();
    } catch (e) {
      setErr(String(e));
    }
  }

  function startEdit(a: AppRow) {
    setEditing(a.package);
    setEditName(a.name);
    setEditBrand(a.brand || '');
  }
  async function saveEdit() {
    if (!editing) return;
    try {
      await updateApp(editing, { name: editName, brand: editBrand });
      setEditing(null);
      await load();
    } catch (e) {
      setErr(String(e));
    }
  }

  function toggleApplovin(a: AppRow) {
    if (expandedApplovin === a.package) {
      setExpandedApplovin(null);
      return;
    }
    setExpandedApplovin(a.package);
    setApplovinDraft({
      sdkKey: a.applovin?.sdkKey || '',
      rewardUnitId: a.applovin?.rewardUnitId || '',
      interstitialUnitId: a.applovin?.interstitialUnitId || '',
    });
  }
  async function saveApplovin() {
    if (!expandedApplovin) return;
    try {
      await updateApp(expandedApplovin, { applovin: applovinDraft });
      setExpandedApplovin(null);
      await load();
    } catch (e) {
      setErr(String(e));
    }
  }

  function toggleLine(a: AppRow) {
    if (expandedLine === a.package) {
      setExpandedLine(null);
      return;
    }
    setExpandedLine(a.package);
    setLineDraft(
      (a.line?.slots || []).map((s) => ({
        slotId: s.slotId,
        targetCpm: s.targetCpm,
      })),
    );
  }
  async function saveLine() {
    if (!expandedLine) return;
    try {
      const clean = lineDraft
        .filter((s) => s.slotId.trim())
        .slice(0, MAX_LINE_SLOTS)
        .map((s) => ({ slotId: s.slotId.trim(), targetCpm: s.targetCpm }));
      await updateApp(expandedLine, { line: { slots: clean } });
      setExpandedLine(null);
      await load();
    } catch (e) {
      setErr(String(e));
    }
  }

  return (
    <div>
      <h2>アプリマスタ</h2>

      <div className="card">
        <h3>新規追加</h3>
        <form onSubmit={onCreate} className="row">
          <input
            placeholder="パッケージ名（com.kkmstudio.rika6）"
            value={pkg}
            onChange={(e) => setPkg(e.target.value)}
            style={{ minWidth: 300 }}
          />
          <input
            placeholder="アプリ名"
            value={name}
            onChange={(e) => setName(e.target.value)}
            style={{ minWidth: 200 }}
          />
          <input
            placeholder="ブランド（任意）"
            value={brand}
            onChange={(e) => setBrand(e.target.value)}
            style={{ minWidth: 140 }}
          />
          <button disabled={!pkg.trim() || !name.trim()}>追加</button>
        </form>
      </div>

      <div className="card">
        <h3>一覧</h3>
        {err && <div className="error">{err}</div>}
        <table>
          <thead>
            <tr>
              <th>パッケージ</th>
              <th>アプリ名</th>
              <th>ブランド</th>
              <th>シフト</th>
              <th>LINE</th>
              <th>AppLovin</th>
              <th>更新</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {apps.map((a) => (
              <>
                <tr key={a.package}>
                  <td style={{ fontFamily: 'monospace', fontSize: '0.9em' }}>
                    {a.package}
                  </td>
                  <td>
                    {editing === a.package ? (
                      <input
                        value={editName}
                        onChange={(e) => setEditName(e.target.value)}
                      />
                    ) : (
                      a.name
                    )}
                  </td>
                  <td>
                    {editing === a.package ? (
                      <input
                        value={editBrand}
                        onChange={(e) => setEditBrand(e.target.value)}
                      />
                    ) : (
                      <span className="muted">{a.brand || '—'}</span>
                    )}
                  </td>
                  <td>
                    <button
                      className="ghost small"
                      title={a.enabled === false ? 'クリックでシフト対象に戻す' : 'クリックでシフトから除外'}
                      style={{
                        background:
                          a.enabled === false ? 'transparent' : '#2e7d32',
                        color: a.enabled === false ? '#c00' : '#fff',
                        border:
                          a.enabled === false
                            ? '1px solid #c00'
                            : '1px solid #2e7d32',
                      }}
                      onClick={async () => {
                        const next = !(a.enabled !== false);
                        try {
                          await updateApp(a.package, { enabled: next });
                          await load();
                        } catch (e) {
                          setErr(String(e));
                        }
                      }}
                    >
                      {a.enabled === false ? '除外中' : '対象'}
                    </button>
                  </td>
                  <td>
                    <button
                      className="ghost small"
                      onClick={() => toggleLine(a)}
                    >
                      {expandedLine === a.package
                        ? '閉じる'
                        : `${a.line?.slots?.length || 0} 個`}
                    </button>
                  </td>
                  <td>
                    <button
                      className="ghost small"
                      onClick={() => toggleApplovin(a)}
                    >
                      {expandedApplovin === a.package
                        ? '閉じる'
                        : a.applovin?.rewardUnitId
                          ? '編集'
                          : '未設定'}
                    </button>
                  </td>
                  <td className="muted">{a.updatedAt}</td>
                  <td>
                    {editing === a.package ? (
                      <div className="row" style={{ gap: 4 }}>
                        <button className="small" onClick={saveEdit}>
                          保存
                        </button>
                        <button
                          className="ghost small"
                          onClick={() => setEditing(null)}
                        >
                          取消
                        </button>
                      </div>
                    ) : (
                      <button
                        className="ghost small"
                        onClick={() => startEdit(a)}
                      >
                        編集
                      </button>
                    )}
                  </td>
                </tr>
                {expandedLine === a.package && (
                  <tr key={`${a.package}-line`}>
                    <td colSpan={8} style={{ background: '#f7f7f8' }}>
                      <div
                        style={{
                          padding: 12,
                          display: 'flex',
                          flexDirection: 'column',
                          gap: 8,
                        }}
                      >
                        <div className="muted" style={{ fontSize: '0.85em' }}>
                          LINE リワード用 waterfall スロット (最大 {MAX_LINE_SLOTS} 個)。
                          targetCPM を高い順にクライアント側で preload → HIT したものを表示。
                          空欄 = 上限なし (最下位)。取得失敗時は 60 秒 cooldown。
                          保存時にサーバ側で CPM 降順ソート。
                        </div>
                        {lineDraft.map((s, i) => (
                          <div
                            key={i}
                            className="row"
                            style={{ gap: 8, alignItems: 'center' }}
                          >
                            <span className="muted" style={{ width: 24 }}>
                              #{i + 1}
                            </span>
                            <input
                              placeholder="Slot ID (例 19707656)"
                              style={{
                                fontFamily: 'monospace',
                                minWidth: 220,
                              }}
                              value={s.slotId}
                              onChange={(e) =>
                                setLineDraft((arr) =>
                                  arr.map((r, j) =>
                                    j === i
                                      ? { ...r, slotId: e.target.value }
                                      : r,
                                  ),
                                )
                              }
                            />
                            <input
                              placeholder="targetCPM (空=上限なし)"
                              type="number"
                              min={0}
                              max={8000}
                              style={{ width: 160 }}
                              value={s.targetCpm ?? ''}
                              onChange={(e) => {
                                const v = e.target.value.trim();
                                const n = v === '' ? null : Number(v);
                                setLineDraft((arr) =>
                                  arr.map((r, j) =>
                                    j === i
                                      ? { ...r, targetCpm: Number.isFinite(n as number) ? n : null }
                                      : r,
                                  ),
                                );
                              }}
                            />
                            <span className="muted">円</span>
                            <button
                              className="ghost small"
                              onClick={() =>
                                setLineDraft((arr) =>
                                  arr.filter((_, j) => j !== i),
                                )
                              }
                            >
                              削除
                            </button>
                          </div>
                        ))}
                        {lineDraft.length < MAX_LINE_SLOTS && (
                          <div>
                            <button
                              className="ghost small"
                              onClick={() =>
                                setLineDraft((arr) => [
                                  ...arr,
                                  { slotId: '', targetCpm: null },
                                ])
                              }
                            >
                              + スロット追加
                            </button>
                          </div>
                        )}
                        <div className="row" style={{ gap: 8 }}>
                          <button onClick={saveLine}>保存</button>
                          <button
                            className="ghost"
                            onClick={() => setExpandedLine(null)}
                          >
                            取消
                          </button>
                        </div>
                      </div>
                    </td>
                  </tr>
                )}
                {expandedApplovin === a.package && (
                  <tr key={`${a.package}-applovin`}>
                    <td colSpan={8} style={{ background: '#f7f7f8' }}>
                      <div
                        style={{
                          padding: 12,
                          display: 'flex',
                          flexDirection: 'column',
                          gap: 8,
                        }}
                      >
                        <div className="muted" style={{ fontSize: '0.85em' }}>
                          アプリ側は初回起動時＋24 時間ごとにここの値を取得し、
                          SharedPreferences にキャッシュします。空欄はハードコード値へフォールバック。
                        </div>
                        <label>
                          SDK Key
                          <input
                            style={{
                              width: '100%',
                              fontFamily: 'monospace',
                              fontSize: '0.85em',
                            }}
                            placeholder="bninEl..."
                            value={applovinDraft.sdkKey || ''}
                            onChange={(e) =>
                              setApplovinDraft((d) => ({
                                ...d,
                                sdkKey: e.target.value,
                              }))
                            }
                          />
                        </label>
                        <label>
                          Reward Ad Unit ID
                          <input
                            style={{
                              width: '100%',
                              fontFamily: 'monospace',
                              fontSize: '0.85em',
                            }}
                            placeholder="e269e5348a3ce918"
                            value={applovinDraft.rewardUnitId || ''}
                            onChange={(e) =>
                              setApplovinDraft((d) => ({
                                ...d,
                                rewardUnitId: e.target.value,
                              }))
                            }
                          />
                        </label>
                        <label>
                          Interstitial Ad Unit ID
                          <input
                            style={{
                              width: '100%',
                              fontFamily: 'monospace',
                              fontSize: '0.85em',
                            }}
                            placeholder="（未発行なら空欄）"
                            value={applovinDraft.interstitialUnitId || ''}
                            onChange={(e) =>
                              setApplovinDraft((d) => ({
                                ...d,
                                interstitialUnitId: e.target.value,
                              }))
                            }
                          />
                        </label>
                        <div className="row" style={{ gap: 8 }}>
                          <button onClick={saveApplovin}>保存</button>
                          <button
                            className="ghost"
                            onClick={() => setExpandedApplovin(null)}
                          >
                            取消
                          </button>
                        </div>
                      </div>
                    </td>
                  </tr>
                )}
              </>
            ))}
            {apps.length === 0 && (
              <tr>
                <td colSpan={8} className="muted">
                  登録済みアプリはありません
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
